import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Plus, Trash2, Save, Sparkles, Upload, X, ArrowLeft, Image } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import './FlashcardBuilder.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function FlashcardBuilder() {
  const navigate = useNavigate();
  const { user } = React.useContext(AuthContext);
  const [setData, setSetData] = useState({
    title: '',
    timer_setting: 'off',
    cards: [{ front_text: '', back_text: '', front_image: null, back_image: null }]
  });
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiNumCards, setAiNumCards] = useState(8);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null); // Track which image is uploading
  
  // Refs for file inputs
  const frontImageRefs = useRef([]);
  const backImageRefs = useRef([]);
  
  const addCard = () => setSetData({ ...setData, cards: [...setData.cards, { front_text: '', back_text: '', front_image: null, back_image: null }] });
  
  const deleteCard = (index) => setSetData({ ...setData, cards: setData.cards.filter((_, i) => i !== index) });
  
  const updateCard = (index, field, value) => {
    const newCards = [...setData.cards];
    newCards[index][field] = value;
    setSetData({ ...setData, cards: newCards });
  };
  
  // Handle image upload from device
  const handleImageUpload = async (index, side, event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setUploadingImage(`${index}-${side}`);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update the card with the image URL
      const field = side === 'front' ? 'front_image' : 'back_image';
      updateCard(index, field, response.data.url);
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Image upload failed:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(null);
    }
  };
  
  // Remove image from card
  const removeImage = (index, side) => {
    const field = side === 'front' ? 'front_image' : 'back_image';
    updateCard(index, field, null);
  };
  
  const saveSet = async () => {
    if (!setData.title) return toast.error('Please enter a title');
    if (setData.cards.some(c => !c.front_text || !c.back_text)) return toast.error('Please fill all cards');
    
    setSaving(true);
    try {
      await axios.post(`${API}/flashcards`, setData, { withCredentials: true });
      toast.success('Flashcard set saved!');
      navigate('/flashcard-hub');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save flashcard set');
    } finally {
      setSaving(false);
    }
  };
  
  const generateAICards = async () => {
    if (!aiTopic) return toast.error('Please enter a topic');
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/ai/generate-flashcards`, { topic: aiTopic, num_cards: aiNumCards }, { withCredentials: true });
      toast.success('AI Flashcards generated!');
      setAiDialogOpen(false);
      setAiTopic('');
      setSetData({ ...setData, cards: response.data.cards.map(c => ({ front_text: c.front_text, back_text: c.back_text, front_image: null, back_image: null })) });
    } catch (error) {
      console.error('AI generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate cards');
    } finally {
      setGenerating(false);
    }
  };
  
  return (
    <div className="flashcard-builder-page" data-testid="flashcard-builder">
      <div className="builder-container">
        <div className="builder-header">
          <div className="header-left">
            <Button 
              onClick={() => navigate('/flashcard-hub')} 
              variant="ghost" 
              className="back-btn"
              data-testid="back-button"
            >
              <ArrowLeft size={20} />
              Back
            </Button>
            <h1>📚 Flashcard Builder</h1>
          </div>
          <div className="header-actions">
            <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
              <DialogTrigger asChild>
                <Button className="ai-btn" data-testid="ai-generator-btn">
                  <Sparkles size={20} />
                  AI Card Generator
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>AI Flashcard Generator</DialogTitle></DialogHeader>
                <div className="ai-form">
                  <div className="form-group">
                    <Label>Topic</Label>
                    <Input 
                      value={aiTopic} 
                      onChange={(e) => setAiTopic(e.target.value)} 
                      placeholder="e.g., Vocabulary, Math..." 
                      data-testid="ai-topic-input"
                    />
                  </div>
                  <div className="form-group">
                    <Label>Number of Cards</Label>
                    <Input 
                      type="number" 
                      min="4" 
                      max="20" 
                      value={aiNumCards} 
                      onChange={(e) => setAiNumCards(parseInt(e.target.value))} 
                      data-testid="ai-num-cards-input"
                    />
                  </div>
                  <Button 
                    onClick={generateAICards} 
                    disabled={generating} 
                    className="generate-btn"
                    data-testid="generate-ai-btn"
                  >
                    {generating ? 'Generating...' : 'Generate Cards'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button 
              onClick={saveSet} 
              className="save-btn" 
              disabled={saving}
              data-testid="save-btn"
            >
              <Save size={20} />
              {saving ? 'Saving...' : 'Save Set'}
            </Button>
          </div>
        </div>
        
        <div className="set-settings">
          <Input 
            value={setData.title} 
            onChange={(e) => setSetData({ ...setData, title: e.target.value })} 
            placeholder="Flashcard Set Title" 
            className="title-input" 
            data-testid="title-input"
          />
          <div className="setting-group">
            <Label>Timer</Label>
            <Select value={setData.timer_setting} onValueChange={(value) => setSetData({ ...setData, timer_setting: value })}>
              <SelectTrigger data-testid="timer-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="off">OFF</SelectItem>
                <SelectItem value="30s">30 seconds</SelectItem>
                <SelectItem value="1min">1 minute</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="cards-section">
          <h2>Cards ({setData.cards.length})</h2>
          <div className="cards-list">
            {setData.cards.map((card, index) => (
              <div key={index} className="card-editor" data-testid={`card-editor-${index}`}>
                <div className="card-header">
                  <span>Card {index + 1}</span>
                  {setData.cards.length > 1 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => deleteCard(index)}
                      data-testid={`delete-card-${index}`}
                    >
                      <Trash2 size={18} />
                    </Button>
                  )}
                </div>
                <div className="card-sides">
                  {/* Front Side */}
                  <div className="side">
                    <Label>Front</Label>
                    <Textarea 
                      value={card.front_text} 
                      onChange={(e) => updateCard(index, 'front_text', e.target.value)} 
                      placeholder="Front text (question/term)" 
                      rows={3}
                      data-testid={`front-text-${index}`}
                    />
                    
                    {/* Front Image Upload */}
                    <div className="image-upload-section">
                      <input
                        type="file"
                        accept="image/*"
                        ref={el => frontImageRefs.current[index] = el}
                        onChange={(e) => handleImageUpload(index, 'front', e)}
                        style={{ display: 'none' }}
                        data-testid={`front-image-input-${index}`}
                      />
                      
                      {card.front_image ? (
                        <div className="image-preview">
                          <img src={card.front_image} alt="Front" />
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="remove-image-btn"
                            onClick={() => removeImage(index, 'front')}
                            data-testid={`remove-front-image-${index}`}
                          >
                            <X size={16} />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="upload-image-btn"
                          onClick={() => frontImageRefs.current[index]?.click()}
                          disabled={uploadingImage === `${index}-front`}
                          data-testid={`upload-front-image-${index}`}
                        >
                          {uploadingImage === `${index}-front` ? (
                            <>Uploading...</>
                          ) : (
                            <>
                              <Image size={16} />
                              Add Image
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Back Side */}
                  <div className="side">
                    <Label>Back</Label>
                    <Textarea 
                      value={card.back_text} 
                      onChange={(e) => updateCard(index, 'back_text', e.target.value)} 
                      placeholder="Back text (answer/definition)" 
                      rows={3}
                      data-testid={`back-text-${index}`}
                    />
                    
                    {/* Back Image Upload */}
                    <div className="image-upload-section">
                      <input
                        type="file"
                        accept="image/*"
                        ref={el => backImageRefs.current[index] = el}
                        onChange={(e) => handleImageUpload(index, 'back', e)}
                        style={{ display: 'none' }}
                        data-testid={`back-image-input-${index}`}
                      />
                      
                      {card.back_image ? (
                        <div className="image-preview">
                          <img src={card.back_image} alt="Back" />
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="remove-image-btn"
                            onClick={() => removeImage(index, 'back')}
                            data-testid={`remove-back-image-${index}`}
                          >
                            <X size={16} />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="upload-image-btn"
                          onClick={() => backImageRefs.current[index]?.click()}
                          disabled={uploadingImage === `${index}-back`}
                          data-testid={`upload-back-image-${index}`}
                        >
                          {uploadingImage === `${index}-back` ? (
                            <>Uploading...</>
                          ) : (
                            <>
                              <Image size={16} />
                              Add Image
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Button onClick={addCard} className="add-card-btn" data-testid="add-card-btn">
            <Plus size={20} />
            Add Card
          </Button>
        </div>
      </div>
    </div>
  );
}

export default FlashcardBuilder;
